import sessionReader from '../store/readHelpers/SessionReader';
import localCache from './LocalCache';
import stateCacheService from './StateCache';

/**
 * LocalCacheSanitiser provides functionality to determine when the local storage
 * should be sanitised (cleared) based on the current session state.
 */
const localCacheSanitiser = {
    /**
     * Determines if all local storage should be sanitised based on the current session state.
     *
     * The function returns true when:
     * 1. The context is an exam, AND
     * 2. The session is NOT resumed, AND
     * 3. Either:
     *    a. The test does not require pre-start steps, OR
     *    b. The test requires pre-start steps but has not been sanitised yet
     *
     * @returns {boolean} True if all local storage should be sanitised, false otherwise.
     */
    shouldSanitiseAllLocalStorage() {
        // Only sanitise for exam contexts
        if (!sessionReader.isContextExam()) {
            return false;
        }

        // Only sanitise for non-resumed sessions
        if (sessionReader.isResumed()) {
            return false;
        }

        // For tests with pre-start steps, check if already sanitised
        const requiresPreStartStep = sessionReader.isTestThatRequiresPreStartStepAndSittingHasNotYetStarted()
            || sessionReader.isTestThatRequiresPreStartStepAndSittingHasAlreadyStarted();

        if (requiresPreStartStep) {
            const lastSanitisationTimestamp = stateCacheService.getStateCacheInstance()
                .getLastLocalCacheSanitisationTimestampForCandidateInTest();

            // Skip if already sanitised for this context
            if (lastSanitisationTimestamp) {
                return false;
            }
        }

        // Sanitise in all other cases
        return true;
    },

    /**
     * Sanitises all local storage if the conditions in shouldSanitiseAllLocalStorage() are met.
     *
     * @returns {boolean} True if local storage was sanitised, false otherwise.
     */
    sanitiseLocalStorageIfNeeded() {
        if (this.shouldSanitiseAllLocalStorage()) {
            localCache.getLocalCacheInstance().removeAll();
            stateCacheService.getStateCacheInstance().setLastLocalCacheSanitisationTimestampForCandidateInTest(Date.now());
            return true;
        }
        return false;
    },
};

export default localCacheSanitiser;
